import { getChromeTabs, getChromeRuntime } from './wrappers';
import { getVerdict } from '../navigation/verdict';
import { ProcessUrlResponse } from '../types';
import {
  isBlockerPage,
  extractOriginalUrlFromBlockerPage,
} from '../navigation/blockingUtils';
import { isProtectionActive } from '../protectionStatus';

/**
 * Utility functions for managing Chrome tabs
 */

/**
 * Helper function to handle Chrome tabs API callbacks with error checking
 */
const handleTabCallback = (
  resolve: (value: chrome.tabs.Tab | undefined) => void
): ((tab: chrome.tabs.Tab | undefined) => void) => {
  return (tab: chrome.tabs.Tab | undefined) => {
    if (getChromeRuntime().lastError) {
      resolve(undefined);
    } else {
      resolve(tab);
    }
  };
};

/**
 * Closes a tab by its ID
 */
export const closeTab = (tabId: number) => getChromeTabs().remove(tabId);

/**
 * Gets a tab by its ID
 */
export const getTab = (tabId: number) =>
  new Promise<chrome.tabs.Tab | undefined>(resolve => {
    if (tabId < 0) resolve(undefined);
    return getChromeTabs().get(tabId, handleTabCallback(resolve));
  });

/**
 * Gets the currently active tab
 */
export const getCurrentTab = (): Promise<chrome.tabs.Tab | undefined> =>
  new Promise<chrome.tabs.Tab | undefined>(resolve => {
    return getChromeTabs().getCurrent(handleTabCallback(resolve));
  });

/**
 * Creates a new Chrome tab
 */
export const createTab = (
  createProperties: chrome.tabs.CreateProperties
): Promise<chrome.tabs.Tab> => {
  return getChromeTabs().create(createProperties);
};

/**
 * Validates that a tab has the required properties for processing
 */
const isValidTab = (
  tab: chrome.tabs.Tab
): tab is chrome.tabs.Tab & { id: number; url: string } => {
  return !!(tab.id && tab.url);
};

/**
 * Queries Chrome tabs using the promise-based API
 */
const queryTabs = (): Promise<chrome.tabs.Tab[]> => {
  return getChromeTabs().query({});
};

/**
 * Updates a Chrome tab using the promise-based API
 */
export const updateTab = (
  tabId: number,
  updateProperties: chrome.tabs.UpdateProperties
): Promise<chrome.tabs.Tab> => {
  return getChromeTabs().update(tabId, updateProperties);
};

/**
 * Reloads a Chrome tab using the promise-based API
 */
const reloadTab = (
  tabId: number,
  reloadProperties?: chrome.tabs.ReloadProperties
): Promise<void> => {
  return getChromeTabs().reload(tabId, reloadProperties);
};

/**
 * Common function to handle tab verdict checking and action execution
 */
const handleTabWithVerdict = async (
  tab: chrome.tabs.Tab & { id: number; url: string },
  urlToCheck: string,
  shouldDoAction: (verdict: ProcessUrlResponse) => boolean,
  action: (tabId: number) => Promise<chrome.tabs.Tab | void>
): Promise<void> => {
  try {
    const processedVerdict = await getVerdict(urlToCheck, true);
    const verdict = processedVerdict.verdict;

    if (shouldDoAction(verdict)) {
      try {
        await action(tab.id);
      } catch (error) {
        console.warn(`Failed to execute action for tab ${tab.id}:`, error);
      }
    }
  } catch (error) {
    console.warn(
      `Failed to get verdict for ${urlToCheck}, skipping action`,
      error
    );
  }
};

/**
 * Handles a blocker page tab by checking if the original URL should be unblocked
 */
const handleBlockerPageTab = async (
  tab: chrome.tabs.Tab,
  isProtectionActive = true
): Promise<void> => {
  if (!isValidTab(tab)) {
    return;
  }

  const originalUrl = extractOriginalUrlFromBlockerPage(tab.url);

  if (!originalUrl) {
    return;
  }

  if (!isProtectionActive) {
    // When protection is not active, always unblock without checking verdict
    await updateTab(tab.id, { url: originalUrl });
  } else {
    // Normal processing: check verdict before unblocking
    await handleTabWithVerdict(
      tab,
      originalUrl,
      verdict =>
        verdict === ProcessUrlResponse.ALLOW ||
        verdict === ProcessUrlResponse.IGNORE,
      tabId => updateTab(tabId, { url: originalUrl })
    );
  }
};

/**
 * Handles a regular tab by checking if it needs to be refreshed
 */
const handleRegularTab = async (
  tab: chrome.tabs.Tab,
  isProtectionActive = true
): Promise<void> => {
  if (!isValidTab(tab) || !isProtectionActive) {
    return;
  }

  await handleTabWithVerdict(
    tab,
    tab.url,
    verdict =>
      verdict !== ProcessUrlResponse.ALLOW &&
      verdict !== ProcessUrlResponse.IGNORE,
    tabId => reloadTab(tabId, { bypassCache: true })
  );
};

/**
 * Processes a single tab to determine if it needs updating
 */
const processTab = async (
  tab: chrome.tabs.Tab,
  isProtectionActive = true
): Promise<void> => {
  if (!isValidTab(tab)) {
    return;
  }

  if (isBlockerPage(tab.url)) {
    await handleBlockerPageTab(tab, isProtectionActive);
  } else {
    await handleRegularTab(tab, isProtectionActive);
  }
};

/**
 * Updates the state of all affected tabs based on current rules
 * @param context - Optional context string describing why this function was called
 */
export const updateTabsStateIfNeeded = async (
  context?: string
): Promise<void> => {
  const protectionActive = isProtectionActive();

  console.info(
    `updateTabsStateIfNeeded executed${
      context ? ` (context: ${context})` : ''
    }`,
    {
      context: context || 'unknown',
      protectionActive,
    }
  );

  try {
    const tabs = await queryTabs();

    // Process all tabs in parallel, passing protection status
    await Promise.all(tabs.map(tab => processTab(tab, isProtectionActive())));
  } catch (error) {
    console.error('Failed to update tab states:', error);
    throw error;
  }
};
